import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const toastTypes = {
  success: { icon: CheckCircle, className: 'bg-green-500 text-white' },
  error: { icon: AlertCircle, className: 'bg-red-500 text-white' },
  warning: { icon: AlertTriangle, className: 'bg-yellow-500 text-white' },
  info: { icon: Info, className: 'bg-blue-500 text-white' },
};

const Toast = ({ id, type, title, message, onRemove }) => {
  const { icon: Icon, className } = toastTypes[type] || toastTypes.info;

  return (
    <div
      className={cn(
        'flex items-start space-x-3 p-4 rounded-lg shadow-lg border border-border bg-card text-foreground',
        'animate-in slide-in-from-right-full duration-300'
      )}
    >
      <Icon className={cn('w-5 h-5 mt-0.5 flex-shrink-0', className)} />

      <div className="flex-1 min-w-0">
        {title && <p className="text-sm font-medium text-foreground">{title}</p>}
        {message && <p className="text-sm text-muted-foreground mt-1">{message}</p>}
      </div>

      <button
        onClick={() => onRemove(id)}
        className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
        aria-label="Close toast"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    ({ type = 'info', title, message, duration = 5000 }) => {
      const id = Date.now() + Math.random();
      const newToast = { id, type, title, message };

      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  const success = useCallback(
    (title, message) => addToast({ type: 'success', title, message }),
    [addToast]
  );
  const error = useCallback(
    (title, message) => addToast({ type: 'error', title, message }),
    [addToast]
  );
  const warning = useCallback(
    (title, message) => addToast({ type: 'warning', title, message }),
    [addToast]
  );
  const info = useCallback(
    (title, message) => addToast({ type: 'info', title, message }),
    [addToast]
  );

  return (
    <ToastContext.Provider value={{ addToast, success, error, warning, info, removeToast }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full">
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default Toast;
